import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { getSetupStatus, setSetupType } from "../../api/operatorSetup";
import { tierExplained, tierLabel } from "../../domain/labels";

const TYPES = ["ISP", "VNO", "OPERATOR", "NETWORK_OPERATOR"];

/**
 * The Owner's own type and upstream (owner 2026-10-31). Verified legal name and
 * PAN stay locked; the server refuses a change once invoices are issued.
 */
export function OwnerTypeCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["setup", ns], retry: false, queryFn: ({ signal }) => getSetupStatus(identity, ns, signal) });
  const [type, setType] = useState("");
  const [upstream, setUpstream] = useState("");
  useEffect(() => {
    if (q.data) {
      setType(q.data.operatortype);
      setUpstream(q.data.upstream ?? "");
    }
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => setSetupType(identity, ns, type, upstream),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["setup", ns] });
      void qc.invalidateQueries({ queryKey: ["operator", ns] });
    },
  });
  if (!q.data || !TYPES.includes(q.data.operatortype)) return null;
  return (
    <Paper sx={{ p: 2, mb: 2 }} data-testid="owner-type-card">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Provider type and upstream</Typography>
      <Stack spacing={2}>
        <TextField select size="small" label="Provider type" value={type} helperText={tierExplained(type)} onChange={(e) => setType(e.target.value)}>
          {TYPES.map((t) => <MenuItem key={t} value={t}>{tierLabel(t)}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Upstream namespace (optional)" value={upstream} placeholder="net.example.isp"
          helperText="An ISP or VNO already on AnkhOS. Ankh's team checks licences and upstream details offline."
          onChange={(e) => setUpstream(e.target.value)} />
        {save.isError && <Alert severity="error">{save.error instanceof Error ? save.error.message : "Could not save."}</Alert>}
        {save.isSuccess && !save.isPending && <Alert severity="success">Saved.</Alert>}
        <Button variant="contained" sx={{ alignSelf: "flex-start" }} disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
      </Stack>
    </Paper>
  );
}
